import { clinicalChanges, clinicalEqual } from './clinicalMerge';

const metadata = new Set(['id', 'referenceid', 'clientrequestid', 'visittype', 'offlinedraftid', 'offlinestatus',
  'createdat', 'updatedat', 'updatedatutc', 'baseversion', 'revision', 'expectedrevision']);
const normalize = value => {
  if (Array.isArray(value)) return value.map(normalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).filter(([key]) => !key.startsWith('__') && !metadata.has(key.toLowerCase()))
    .map(([key, item]) => [key.toLowerCase(), normalize(item)]));
};
export const recoveryState = copy => copy.chartType === 'labor-record' ? copy.state?.formData : copy.state;
const recoveryBase = copy => copy.chartType === 'labor-record' ? copy.base?.formData || copy.base : copy.base;
export const recoveryType = type => String(type || '').replace(/[-_]/g, '').replace(/visit$/i, '').toLowerCase();
export const recoveryRecordId = copy => copy.chartId || copy.state?.currentRecord?.referenceId || copy.state?.currentRecord?.id
  || copy.state?.referenceId || copy.state?.ReferenceId || copy.state?.id || copy.state?.Id;

export function recoveryHasNoEdits(copy) {
  if (!recoveryState(copy)) return false;
  if (recoveryBase(copy)) return clinicalChanges(normalize(recoveryBase(copy)), normalize(recoveryState(copy))) === undefined;
  if (recoveryRecordId(copy)) return false;
  // Pre-fix empty create snapshots had no baseline. Date/provider defaults
  // identify the blank form, not an entered clinical note. Unknown values stay.
  const defaults = new Set(['visitdate', 'visittime', 'provider', 'providername', 'pregnancyid', 'pregnancyreferenceid',
    'clientid', 'clientreferenceid', 'babyid', 'babyreferenceid']);
  const empty = value => value == null || value === '' || (Array.isArray(value) && value.length === 0)
    || (value && typeof value === 'object' && !Array.isArray(value) && Object.values(value).every(empty));
  return Object.entries(normalize(recoveryState(copy))).every(([key, value]) => defaults.has(key) || empty(value));
}

export function recoveryMatchesServer(copy, server) {
  if (!recoveryState(copy) || !server) return false;
  const state = normalize(recoveryState(copy));
  const expected = recoveryBase(copy) ? clinicalChanges(normalize(recoveryBase(copy)), state) : state;
  const matches = (desired, actual, path = '') => {
    if (desired && typeof desired === 'object' && !Array.isArray(desired))
      return Object.entries(desired).every(([key, value]) => matches(value, actual?.[key], path ? `${path}.${key}` : key));
    if (typeof actual === 'number' && typeof desired === 'string' && desired.trim() !== '' && Number.isFinite(Number(desired))) return Number(desired) === actual;
    return clinicalEqual(desired, actual, path);
  };
  return expected === undefined || matches(expected, normalize(copy.chartType === 'labor-record' ? server.formData || server : server));
}

export function recoveryIsSuperseded(copy, resource) {
  if (recoveryHasNoEdits(copy)) return true;
  if (!resource?.base || resource.revision === 0 || resource.revision < (copy.baseRevision || 0)) return false;
  if (recoveryMatchesServer(copy, resource.base)) return true;
  // Another birth/assessment save is not proof that this editor's changes synced.
  if (['labor-episode', 'postpartum-assessment'].includes(copy.chartType)) return false;
  // Only a record's server modification time can supersede an older snapshot.
  // A chart preparation/download time says nothing about when it was saved.
  const server = resource.base.primaryBirthEvent || resource.base;
  const updatedAt = Date.parse(server.updatedAt || server.UpdatedAt || server.updatedAtUtc || server.UpdatedAtUtc || '');
  const capturedAt = Date.parse(copy.capturedAt || copy.locallySavedAt || '');
  return Number.isFinite(updatedAt) && Number.isFinite(capturedAt) && updatedAt > capturedAt;
}
